import { buildBlockTree, collectDescendantIds } from '@/lib/blocks/tree';
import { readRecord } from '@/lib/prop-utils';
import {
  getBlockSlot,
  isSpecialPageLayoutSection,
  SPECIAL_PAGE_BODY_SLOT,
} from '@/lib/special-page-layouts';
import type { PageBlock } from '@/types/block';
import {
  type PageEditorAction,
  PageEditorActionType,
  type PageEditorAddBlockAction,
  type PageEditorDeleteBlockAction,
  type PageEditorUpdateBlockAction,
} from '@/types/page-editor-actions';
import type { PageSection } from '@/types/page-section';
import { type AutosaveBlockPayload, buildBlockPayload } from './autosave-block-payload';

// Mirrors the backend list of prop keys we preserve when harvesting edits from a
// slot-bearing block into `slot_content` (e.g. content + media identifiers).
const SLOT_PRESERVED_KEYS: ReadonlyArray<string> = [
  'content',
  'image',
  'assetId',
  'url',
  'alt',
  'shape',
  'aspect',
  'fit',
  'fullBleed',
  'focusX',
  'focusY',
  'width',
  'height',
  'imageSize',
  'text_align',
  'render_style',
];
const SLOT_CLEARABLE_MEDIA_KEYS = new Set([
  'image',
  'assetId',
  'url',
  'width',
  'height',
  'imageSize',
]);

type AutosaveSectionUpdates = Partial<Omit<PageSection, 'id' | 'blocks' | 'uiKey'>>;

type AutosaveAddSectionPayload = Omit<PageSection, 'blocks' | 'uiKey'> & {
  operation: 'add';
  blocks?: AutosaveBlockPayload[];
};
type AutosaveUpdateSectionPayload = AutosaveSectionUpdates & {
  id: string;
  operation: 'update';
  blocks?: AutosaveBlockPayload[];
  slotContent?: Record<string, Record<string, unknown>>;
};
type AutosaveDeleteSectionPayload = { id: string; operation: 'delete' };
export type AutosaveSectionPayload =
  | AutosaveAddSectionPayload
  | AutosaveUpdateSectionPayload
  | AutosaveDeleteSectionPayload;

/**
 * Per-entity lifecycle across an action batch.
 *
 * - `firstMutation`: the entity's first action in this batch. `'add'` means
 *   "entity was born in this batch" and distinguishes ADD->DELETE->ADD (still
 *   net 'add') from DELETE->ADD (undo-of-delete for a pre-existing entity,
 *   net 'none' - backend already has it).
 * - `netMutation`: the collapsed op after replaying the batch. `'none'`
 *   means the actions cancel out and nothing should be sent.
 */
type LifecycleFirstMutation = 'add' | 'delete' | 'update';
type LifecycleNetMutation = 'add' | 'delete' | 'update' | 'none';

interface EntityLifecycle {
  firstMutation: LifecycleFirstMutation;
  netMutation: LifecycleNetMutation;
}

export function getAffectedSectionIds(actions: PageEditorAction[]): Set<string> {
  const sectionIds = new Set<string>();

  for (const action of actions) {
    if (action.type === PageEditorActionType.PAGE_SECTION_REORDER) {
      for (const id of action.payload.sectionIds) {
        sectionIds.add(id);
      }
    } else if ('sectionId' in action.payload) {
      sectionIds.add(action.payload.sectionId);
    }
  }

  return sectionIds;
}

export function hasStructuralSectionChanges(actions: PageEditorAction[]): boolean {
  return actions.some(
    (action) =>
      action.type === PageEditorActionType.PAGE_SECTION_ADD ||
      action.type === PageEditorActionType.PAGE_SECTION_DELETE ||
      action.type === PageEditorActionType.PAGE_SECTION_REORDER,
  );
}

function evolveLifecycle(
  current: EntityLifecycle | undefined,
  mutation: LifecycleFirstMutation,
): EntityLifecycle {
  const firstMutation = current?.firstMutation ?? mutation;
  const createdInCurrentBatch = firstMutation === 'add';
  const previousNetMutation = current?.netMutation ?? 'none';

  if (mutation === 'update') {
    if (previousNetMutation === 'add') {
      return { firstMutation, netMutation: 'add' };
    }

    if (previousNetMutation === 'delete') {
      return { firstMutation, netMutation: 'delete' };
    }

    return { firstMutation, netMutation: 'update' };
  }

  if (mutation === 'delete') {
    if (previousNetMutation === 'add' && createdInCurrentBatch) {
      return { firstMutation, netMutation: 'none' };
    }

    return { firstMutation, netMutation: 'delete' };
  }

  if (previousNetMutation === 'delete' && !createdInCurrentBatch) {
    return { firstMutation, netMutation: 'none' };
  }

  return { firstMutation, netMutation: 'add' };
}

function getSectionLifecycles(actions: PageEditorAction[]): Map<string, EntityLifecycle> {
  const lifecycles = new Map<string, EntityLifecycle>();

  actions.forEach((action) => {
    if (action.type === PageEditorActionType.PAGE_SECTION_ADD) {
      const sectionId = action.payload.section.id;
      lifecycles.set(sectionId, evolveLifecycle(lifecycles.get(sectionId), 'add'));
      return;
    }

    if (action.type === PageEditorActionType.PAGE_SECTION_DELETE) {
      const sectionId = action.payload.sectionId;
      lifecycles.set(sectionId, evolveLifecycle(lifecycles.get(sectionId), 'delete'));
      return;
    }

    if (action.type === PageEditorActionType.PAGE_SECTION_UPDATE) {
      const sectionId = action.payload.sectionId;
      lifecycles.set(sectionId, evolveLifecycle(lifecycles.get(sectionId), 'update'));
    }
  });

  return lifecycles;
}

export function getBlockActionsBySection(
  actions: PageEditorAction[],
): Map<
  string,
  Array<PageEditorAddBlockAction | PageEditorUpdateBlockAction | PageEditorDeleteBlockAction>
> {
  const map = new Map<
    string,
    Array<PageEditorAddBlockAction | PageEditorUpdateBlockAction | PageEditorDeleteBlockAction>
  >();

  actions.forEach((action) => {
    if (
      action.type !== PageEditorActionType.PAGE_BLOCK_ADD &&
      action.type !== PageEditorActionType.PAGE_BLOCK_UPDATE &&
      action.type !== PageEditorActionType.PAGE_BLOCK_DELETE
    ) {
      return;
    }

    const sectionId = action.payload.sectionId;
    const sectionActions = map.get(sectionId) || [];
    sectionActions.push(action);
    map.set(sectionId, sectionActions);
  });

  return map;
}

export function getSectionUpdatesBySection(
  actions: PageEditorAction[],
): Map<string, AutosaveSectionUpdates> {
  const map = new Map<string, AutosaveSectionUpdates>();

  actions.forEach((action) => {
    if (action.type !== PageEditorActionType.PAGE_SECTION_UPDATE) {
      return;
    }

    const existingUpdates = map.get(action.payload.sectionId) || {};
    const nextUpdates: AutosaveSectionUpdates = {
      ...existingUpdates,
      ...action.payload.updates,
    };

    if (readRecord(existingUpdates.props) && readRecord(action.payload.updates.props)) {
      nextUpdates.props = {
        ...existingUpdates.props,
        ...action.payload.updates.props,
      };
    }

    map.set(action.payload.sectionId, nextUpdates);
  });

  return map;
}

function findBlockById(blocks: PageBlock[] | undefined, blockId: string): PageBlock | undefined {
  if (!blocks) {
    return undefined;
  }
  for (const block of blocks) {
    if (block.id === blockId) {
      return block;
    }
  }
  return undefined;
}

function preservedSlotProps(props: Record<string, unknown> | undefined): Record<string, unknown> {
  const record = readRecord(props) ? props : undefined;
  if (!record) {
    return {};
  }
  const out: Record<string, unknown> = {};
  for (const key of SLOT_PRESERVED_KEYS) {
    if (key in record && (record[key] != null || SLOT_CLEARABLE_MEDIA_KEYS.has(key))) {
      out[key] = record[key];
    }
  }
  return out;
}

// Blocks the special-page template renders on the fly carry a synthetic id and have no persisted row.
function isVirtualBlockId(blockId: string): boolean {
  return blockId.startsWith('virtual-');
}

function getSpecialBodyGroupBlockIds(section: PageSection): Set<string> {
  const blocks = section.blocks ?? [];
  const root = blocks.find((block) => getBlockSlot(block) === SPECIAL_PAGE_BODY_SLOT);
  if (!root) return new Set();

  const { childrenByParent } = buildBlockTree(blocks);
  return new Set([root.id, ...collectDescendantIds(root.id, childrenByParent)]);
}

/**
 * Translates slot-bearing block edits in a special-page-layout section into a
 * slot_content patch keyed by slot name. AI assistant updates can arrive as
 * fresh virtual blocks with new ids, so slot-bearing add operations are also
 * harvested from the section's current rendered blocks.
 */
function buildSlotContentPatch({
  section,
  blockPayload,
  bodyGroupBlockIds = new Set(),
}: {
  section: PageSection;
  blockPayload: AutosaveBlockPayload[] | undefined;
  bodyGroupBlockIds?: ReadonlySet<string>;
}): Record<string, Record<string, unknown>> | undefined {
  if (!blockPayload || blockPayload.length === 0) {
    return undefined;
  }

  const patch: Record<string, Record<string, unknown>> = {};

  for (const entry of blockPayload) {
    if (entry.operation === 'delete') {
      continue;
    }

    const block = findBlockById(section.blocks, entry.id);
    const sourceProps = entry.operation === 'add' ? block?.props : preservedSlotProps(entry.props);
    if (!block || !sourceProps) {
      continue;
    }
    if (bodyGroupBlockIds.has(block.id)) {
      continue;
    }

    const slot = readRecord(block.props) ? (block.props.slot as string | undefined) : undefined;
    if (!slot) {
      continue;
    }

    const incoming = preservedSlotProps(sourceProps);
    if (Object.keys(incoming).length === 0) {
      continue;
    }

    patch[slot] = { ...(patch[slot] || {}), ...incoming };
  }

  return Object.keys(patch).length > 0 ? patch : undefined;
}

/**
 * Builds the PATCH body `sections` array from queued actions and the **current**
 * page state (`currentSections` is read from the editor store at flush time).
 *
 * **Reorder / row positions:** `PAGE_SECTION_REORDER` actions may list only a
 * subset of section ids (see page-editor-store `reorderSections`), but when
 * `hasStructuralSectionChanges(actions)` is true, we still emit an update for
 * **every** section and set `rowPosition` from `currentSections`. The server
 * (`Pages::Versions::Saver#reorder_all_sections!`) then normalizes sequential
 * positions. So the narrow `sectionIds` on the action is an optimization for
 * queuing, not the full source of truth for the autosave payload.
 */
export function buildSectionPayload({
  actions,
  currentSections,
}: {
  actions: PageEditorAction[];
  currentSections: PageSection[];
}): AutosaveSectionPayload[] {
  const currentSectionMap = new Map(currentSections.map((section) => [section.id, section]));
  const structuralChange = hasStructuralSectionChanges(actions);
  const sectionLifecycles = getSectionLifecycles(actions);
  const blockActionsBySection = getBlockActionsBySection(actions);
  const sectionUpdatesBySection = getSectionUpdatesBySection(actions);

  const targetSectionIds = structuralChange
    ? currentSections.map((section) => section.id)
    : Array.from(getAffectedSectionIds(actions));

  const sectionPayload: AutosaveSectionPayload[] = [];

  for (const sectionId of targetSectionIds) {
    const section = currentSectionMap.get(sectionId);
    const lifecycle = sectionLifecycles.get(sectionId);
    if (!section) {
      if (lifecycle?.netMutation === 'delete') {
        sectionPayload.push({
          id: sectionId,
          operation: 'delete',
        });
      }
      continue;
    }

    const isSectionAdd = lifecycle ? lifecycle.netMutation === 'add' : false;
    const blockPayload = buildBlockPayload({
      section,
      isSectionAdd,
      actions: blockActionsBySection.get(sectionId) || [],
    });

    if (isSectionAdd) {
      const { blocks: _blocks, uiKey: _uiKey, ...sectionWithoutBlocks } = section;
      sectionPayload.push({
        ...sectionWithoutBlocks,
        ...(blockPayload ? { blocks: blockPayload } : {}),
        operation: 'add',
      });
      continue;
    }

    const {
      blocks: sectionUpdateBlocks,
      ...sectionUpdates
    }: AutosaveSectionUpdates & {
      blocks?: PageSection['blocks'];
    } = sectionUpdatesBySection.get(sectionId) || {};

    if (structuralChange) {
      sectionUpdates.rowPosition = section.rowPosition;
    }

    const mergedBlocks = sectionUpdateBlocks
      ? Array.from(
          new Map([
            ...sectionUpdateBlocks.map(
              ({ uiKey: _uiKey, ...block }) =>
                [block.id, block as unknown as AutosaveBlockPayload] as const,
            ),
            ...(blockPayload ?? []).map((entry) => [entry.id, entry] as const),
          ]).values(),
        )
      : blockPayload;

    // Special-page shells remain slot-backed, while descendants of the marked
    // body group persist through the normal block mutation pipeline.
    if (isSpecialPageLayoutSection(section)) {
      // A virtual body block has no row to update, so it persists through slot_content instead.
      const bodyGroupBlockIds = new Set(
        [...getSpecialBodyGroupBlockIds(section)].filter((id) => !isVirtualBlockId(id)),
      );
      const slotPatch = buildSlotContentPatch({
        section,
        blockPayload: mergedBlocks,
        bodyGroupBlockIds,
      });
      const persistedBodyBlocks = mergedBlocks?.filter((entry) => {
        if (entry.operation === 'delete') {
          return !isVirtualBlockId(entry.id);
        }

        const block = findBlockById(section.blocks, entry.id);
        return Boolean(
          block &&
            bodyGroupBlockIds.has(block.id) &&
            getBlockSlot(block) !== SPECIAL_PAGE_BODY_SLOT,
        );
      });

      if (
        Object.keys(sectionUpdates).length === 0 &&
        !slotPatch &&
        (!persistedBodyBlocks || persistedBodyBlocks.length === 0)
      ) {
        continue;
      }

      sectionPayload.push({
        id: section.id,
        operation: 'update',
        ...sectionUpdates,
        ...(slotPatch ? { slotContent: slotPatch } : {}),
        ...(persistedBodyBlocks && persistedBodyBlocks.length > 0
          ? { blocks: persistedBodyBlocks }
          : {}),
      });
      continue;
    }

    if (Object.keys(sectionUpdates).length === 0 && !mergedBlocks) {
      continue;
    }

    sectionPayload.push({
      id: section.id,
      operation: 'update',
      ...sectionUpdates,
      ...(mergedBlocks ? { blocks: mergedBlocks } : {}),
    });
  }

  if (structuralChange) {
    for (const [deletedSectionId, lifecycle] of sectionLifecycles.entries()) {
      if (lifecycle.netMutation !== 'delete' || currentSectionMap.has(deletedSectionId)) {
        continue;
      }

      sectionPayload.push({
        id: deletedSectionId,
        operation: 'delete',
      });
    }
  }

  return sectionPayload;
}
